/**
 * Willtec Hero Slider Custom Element
 * Handles full-width responsive banner slider with fade transitions, touch gestures,
 * autoplay, pause-on-hover, accessibility and theme editor event integration.
 */
(() => {
  if (customElements.get('willtec-hero-slider')) return;

  class WilltecHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
  }

  connectedCallback() {
    this.slides = this.querySelectorAll('[data-slide]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dots = this.querySelectorAll('[data-dot]');

    if (this.slides.length <= 1) return;

    const autoplayAttr = this.getAttribute('autoplay');
    this.autoplay = autoplayAttr === 'true' || autoplayAttr === '1' || autoplayAttr === '';
    this.interval = parseInt(this.getAttribute('interval'), 10) || 5000;
    this.pauseOnHover = this.getAttribute('pause-on-hover') === 'true';

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.handlePrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.handleNext());

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.resetAutoplay();
      });
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.handleMouseEnter());
      this.addEventListener('mouseleave', () => this.handleMouseLeave());
    }

    this.addEventListener('focusin', () => this.handleFocusIn());
    this.addEventListener('focusout', () => this.handleFocusOut());

    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());

    this.initTouch();
    this.startAutoplay();
    this.updateAccessibility();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.isPaused) {
        this.next();
      }
    }, this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.startAutoplay();
  }

  goTo(index) {
    if (this.slides.length <= 1) return;

    const total = this.slides.length;
    const currentSlide = this.slides[this.currentIndex];
    
    currentSlide.classList.remove('active');
    currentSlide.style.opacity = '0';
    currentSlide.style.pointerEvents = 'none';

    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('bg-[#FE3B40]', 'w-8');
      this.dots[this.currentIndex].classList.add('bg-white/50', 'w-3');
      this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
    }

    if (index >= total) {
      this.currentIndex = 0;
    } else if (index < 0) {
      this.currentIndex = total - 1;
    } else {
      this.currentIndex = index;
    }

    const nextSlide = this.slides[this.currentIndex];
    nextSlide.classList.add('active');
    nextSlide.style.opacity = '1';
    nextSlide.style.pointerEvents = 'auto';

    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('bg-white/50', 'w-3');
      this.dots[this.currentIndex].classList.add('bg-[#FE3B40]', 'w-8');
      this.dots[this.currentIndex].setAttribute('aria-selected', 'true');
    }

    this.updateAccessibility();
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  handleNext() {
    this.next();
    this.resetAutoplay();
  }

  handlePrev() {
    this.prev();
    this.resetAutoplay();
  }

  handleMouseEnter() {
    this.isPaused = true;
  }

  handleMouseLeave() {
    this.isPaused = false;
  }

  handleFocusIn() {
    this.isPaused = true;
  }

  handleFocusOut() {
    this.isPaused = false;
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  updateAccessibility() {
    this.slides.forEach((slide, idx) => {
      if (idx === this.currentIndex) {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
    });
  }

  initTouch() {
    let startX = 0;
    let endX = 0;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.handleNext();
        } else {
          this.handlePrev();
        }
      }
    }, { passive: true });
  }

  onBlockSelect(blockId) {
    const slideToSelect = this.querySelector(`[data-block-id="${blockId}"]`);
    if (slideToSelect) {
      const index = Array.from(this.slides).indexOf(slideToSelect);
      if (index !== -1) {
        this.goTo(index);
        this.stopAutoplay();
      }
    }
  }

  onBlockDeselect() {
    this.startAutoplay();
  }
}

  if (!customElements.get('willtec-hero-slider')) {
    customElements.define('willtec-hero-slider', WilltecHeroSlider);
  }
})();
